import { useQueryClient } from '@tanstack/react-query';
import { isEmpty } from 'radash';
import { useCallback, useEffect } from 'react';

import {
    getMassUpdateStartToastMessage,
    shouldShowMassUpdateStartToastFromEvent,
} from '@/dialogs/mass-update/massUpdateStartToast';
import useRealTimeMessaging from '@/hooks/useRealtimeMessaging';
import { useToast } from '@/hooks/useToast';
import { useTranslation } from '@/hooks/useTranslation';
import { useUser } from '@/hooks/useUser';
import useWatchListService from '@/hooks/useWatchlistService';
import { getInjector } from '@/services/angularjs-bridge';
import { logger } from '@/services/logger';
import { fetchItemData } from '@/services/utils';
import { RealTimeMessagingEvents } from '@/types/RealTimeMessagingEvents';

interface ItemsPage {
    data: any[];
    [key: string]: unknown;
}

interface ItemsInfiniteData {
    pages: ItemsPage[];
    [key: string]: unknown;
}

interface UseItemsListRealtimeUpdatesArgs {
    /** Fired after a mass update finishes so the grid reflects the applied changes. */
    refetch: () => void;
}

/**
 * LAX-10041: real-time event handling for the React browse items list.
 * Mirrors the legacy `items-controller.js#channelMessageReceived` handler
 * (webapp/app/controllers/items-controller.js lines 126-292) so that
 * remote item changes, deletions, validations, publications and mass
 * updates are reflected in the visible grid without a full refetch.
 *
 * Not covered here on purpose:
 *  - ORGANIZATION_VERIFICATION_CHANGED_EVENT — legacy reloads
 *    communication channels which are unrelated to the items grid.
 */
export function useItemsListRealtimeUpdates({ refetch }: UseItemsListRealtimeUpdatesArgs) {

    const queryClient = useQueryClient();
    const { toast } = useToast();
    const { t } = useTranslation();
    const { user } = useUser();
    const { watchListService } = useWatchListService();
    const $angularJSInjector = getInjector();

    // Read $routeParams lazily on each event: route can change while the
    // grid is mounted (category / selection switches keep the same
    // component instance), so a captured value would be stale.
    const readRouteParams = useCallback(() => {
        try {
            return $angularJSInjector.get('$routeParams');
        } catch {
            return {} as Record<string, unknown>;
        }
    // $angularJSInjector is a stable service reference for the app lifetime.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /**
     * Apply a per-item transformation to every cached ['items'] page.
     * Returning the same reference short-circuits the write for that page
     * to avoid needlessly invalidating downstream selectors.
     */
    const patchCachedItems = useCallback(
        (mutate: (item: any) => any) => {
            const allQueries = queryClient.getQueriesData<ItemsInfiniteData>({
                queryKey: ['items'],
            });
            allQueries.forEach(([key, oldData]) => {
                if (!oldData) return;
                let anyChanged = false;
                const newPages = oldData.pages.map((page) => {
                    let pageChanged = false;
                    const newData = page.data.map((item) => {
                        const result = mutate(item);
                        if (result !== item) {
                            pageChanged = true;
                        }
                        return result;
                    });
                    if (!pageChanged) return page;
                    anyChanged = true;
                    return { ...page, data: newData };
                });
                if (anyChanged) {
                    queryClient.setQueryData(key, { ...oldData, pages: newPages });
                }
            });
        },
        [queryClient],
    );

    /** Drop the given primary keys from every cached ['items'] page. */
    const removeCachedItems = useCallback(
        (primaryKeys: string[]) => {
            if (isEmpty(primaryKeys)) return;
            const toRemove = new Set(primaryKeys);
            const allQueries = queryClient.getQueriesData<ItemsInfiniteData>({
                queryKey: ['items'],
            });
            allQueries.forEach(([key, oldData]) => {
                if (!oldData) return;
                let anyChanged = false;
                const newPages = oldData.pages.map((page) => {
                    const filtered = page.data.filter(
                        (item: any) => !toRemove.has(item.primaryKey__),
                    );
                    if (filtered.length === page.data.length) return page;
                    anyChanged = true;
                    return { ...page, data: filtered };
                });
                if (anyChanged) {
                    queryClient.setQueryData(key, { ...oldData, pages: newPages });
                }
            });
        },
        [queryClient],
    );

    /** Collect the set of primary keys currently held in the ['items'] cache. */
    const collectCachedPrimaryKeys = useCallback((): Set<string> => {
        const cached = new Set<string>();
        const allQueries = queryClient.getQueriesData<ItemsInfiniteData>({
            queryKey: ['items'],
        });
        allQueries.forEach(([, oldData]) => {
            oldData?.pages?.forEach((page) => {
                page.data.forEach((item: any) => {
                    if (item?.primaryKey__) {
                        cached.add(item.primaryKey__);
                    }
                });
            });
        });
        return cached;
    }, [queryClient]);

    const handleItemChanged = useCallback(async (data: any) => {

        const { userId, typeOfChange, primaryKeys = [], categories = [] } = data ?? {};
        const currentUserId = user?.userId;

        // Legacy lines 132-135: errors caused by the current user get an
        // explicit growl.error; other users' errors are silent.
        if (typeOfChange === 'error' && userId === currentUserId) {
            const message = data?.message ?? '';
            toast.error(t(message, data));
            return;
        }

        // Legacy line 141: deletions clear the watchlist regardless of
        // whether the current view actually shows the items.
        if (typeOfChange === 'deleted') {
            watchListService?.removeWatchlistItems?.(primaryKeys);
        }

        // Legacy lines 145-157: only touch the visible grid when the changed
        // categories intersect the current view.
        const routeParams = readRouteParams() as {
            selectionId?: unknown;
            category?: string;
        };
        let hasToUpdate = false;
        const routeCategory = routeParams?.category;
        const routeSelectionId = routeParams?.selectionId;
        if (isEmpty(categories) || (routeSelectionId == null && routeCategory == null)) {
            hasToUpdate = true;
        } else if (routeSelectionId != null) {
            const watchlistCategories: string[] =
                watchListService?.getWatchlistCategories?.() ?? [];
            hasToUpdate =
                !isEmpty(watchlistCategories) &&
                categories.some((c: string) => watchlistCategories.includes(c));
        } else if (routeCategory === 'NO_CATEGORY' && categories.includes(null)) {
            hasToUpdate = true;
        } else if (categories.includes(routeCategory)) {
            hasToUpdate = true;
        }

        if (!hasToUpdate) return;

        if (typeOfChange === 'deleted') {
            removeCachedItems(primaryKeys);
            return;
        }

        // Legacy lines 167-189: only reload if at least one of the changed
        // items is already visible in the grid, otherwise a background
        // change to an off-screen item would trigger a needless refetch.
        const cachedPrimaryKeys = collectCachedPrimaryKeys();
        if (cachedPrimaryKeys.size === 0) {
            // Nothing loaded yet - the initial fetch will pick up the change.
            return;
        }
        const affected: string[] = primaryKeys.filter((pk: string) =>
            cachedPrimaryKeys.has(pk),
        );
        if (affected.length === 0) return;

        try {
            const responses = await Promise.all(
                affected.map((pk) => fetchItemData(pk)),
            );
            const byPrimaryKey = new Map<string, any>();
            responses.forEach((item) => {
                if (item?.primaryKey__) {
                    byPrimaryKey.set(item.primaryKey__, item);
                }
            });
            if (byPrimaryKey.size > 0) {
                patchCachedItems((item) => {
                    const fresh = byPrimaryKey.get(item.primaryKey__);
                    return fresh ? { ...item, ...fresh } : item;
                });
            }
        } catch (error) {
            logger.debug('ItemsList realtime reload failed', error);
        }

        // Legacy lines 181-188: warn about updates made by other users when
        // any changed item could not be reconciled with the visible grid.
        const notAllVisible = affected.length < primaryKeys.length;
        if (notAllVisible && userId != null && userId !== currentUserId) {
            toast.warning(t('ITEM.NOTIFICATION.ITEM_UPDATED'));
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, watchListService, patchCachedItems, removeCachedItems, collectCachedPrimaryKeys, readRouteParams]);

    const handleValidationFinished = useCallback((data: any) => {
        // Legacy lines 220-230: patch per-row validation indicators in place.
        // No refetch: the payload already carries the fields the grid needs.
        const validationResults: Record<string, any> | undefined =
            data?.validationResults;
        if (!validationResults) return;
        patchCachedItems((item) => {
            const result = validationResults[item.primaryKey__];
            if (!result) return item;
            return {
                ...item,
                validation_dirty__: false,
                validation__: result.validationErrors,
                validation_warnings__: result.validationWarnings,
                attributeStates__: result.attributeStates,
            };
        });
    }, [patchCachedItems]);

    const handlePublicationTaskChanged = useCallback((data: any) => {
        // Legacy lines 240-291. The backend fires this on task creation,
        // but publishedItems is only populated on successful publication -
        // which is why legacy hard-codes `SUCCESS` (see FIXME on line 259).
        if (data?.typeOfChange !== 'updated') return;
        const publication = data?.publication;
        const publishedItems: string[] | undefined = publication?.publishedItems;
        if (isEmpty(publishedItems)) return;

        const destinationId = String(publication.destinationId);
        const destinations = [destinationId];
        if (publication.subDestination != null) {
            destinations.push(`${destinationId}:${publication.subDestination}`);
        }
        const publishedSet = new Set(publishedItems);
        const publicationType = publication.publicationType;

        patchCachedItems((item) => {
            if (!publishedSet.has(item.primaryKey__)) return item;

            const currentPublications: string[] = item.publications__ ?? [];
            let nextPublications: string[];
            if (publicationType === 'ADD') {
                const merged = new Set(currentPublications);
                destinations.forEach((d) => merged.add(d));
                nextPublications = Array.from(merged);
            } else {
                const removeSet = new Set(destinations);
                nextPublications = currentPublications.filter(
                    (d) => !removeSet.has(d),
                );
            }
            const isPublished = nextPublications.length > 0;
            return {
                ...item,
                publicationStatus__: 'SUCCESS',
                publicationStartedAt__: publication.creationDate,
                publications__: nextPublications,
                publishedAt__: isPublished ? publication.updatedDate : null,
                published__: isPublished,
            };
        });
    }, [patchCachedItems]);

    const handleMassUpdateSummaryChanged = useCallback((data: any) => {
        // Legacy lines 194-219: only notify the user who started the mass
        // update; on FINISHED, refetch so the applied attribute changes
        // show up without a manual reload.
        if (data?.startedBy !== user?.userId) return;
        if (data.status === 'RUNNING') {
            // LAX-10060: the dialog already shows the start toast when the
            // count is known (PK list or browse hit-list). Do not interpolate
            // backend totalCount = -1, and do not emit a second start toast.
            if (!shouldShowMassUpdateStartToastFromEvent(data)) return;
            toast.info(
                getMassUpdateStartToastMessage(t, { count: data.totalCount, capped: false }),
            );
        } else if (data.status === 'FINISHED') {
            toast.info(
                t('MASS_UPDATE_EVENT.FINISHED', { count: data.finishedCount }),
            );
            refetch();
        }
    }, [user, toast, t, refetch]);

    const { subscribe: subscribeItemChanged, unsubscribe: unsubscribeItemChanged } =
        useRealTimeMessaging({
            eventName: RealTimeMessagingEvents.ITEM_CHANGED_EVENT,
            eventHandler: handleItemChanged,
        });

    useRealTimeMessaging({
        eventName: RealTimeMessagingEvents.VALIDATION_FINISHED_EVENT,
        eventHandler: handleValidationFinished,
    });

    useRealTimeMessaging({
        eventName: RealTimeMessagingEvents.PUBLICATION_TASK_CHANGED_EVENT,
        eventHandler: handlePublicationTaskChanged,
    });

    useRealTimeMessaging({
        eventName: RealTimeMessagingEvents.MASS_UPDATE_SUMMARY_CHANGED_EVENT,
        eventHandler: handleMassUpdateSummaryChanged,
    });

    // Match legacy items-controller.js line 35 (subscribe on mount) +
    // line 1747 (unsubscribe on $destroy). The other events are already
    // subscribed globally by application-controller.js#registerMultiple.
    // ChannelService reference-counts subscribers, so co-existing with the
    // legacy items-controller during the migration is safe.
    useEffect(() => {
        subscribeItemChanged();
        return () => {
            unsubscribeItemChanged();
        };
    }, [subscribeItemChanged, unsubscribeItemChanged]);
}
